import {
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Chart as ChartJS,
  ChartData,
  ScriptableContext,
} from "chart.js";
import classNames from "classnames";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { TypedChartComponent } from "react-chartjs-2/dist/types";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import styles from "./AscAnalyseReportSkillsChart.module.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

export interface AscAnalyseReportChartsProps {
  className?: string;
  data: {
    skillId: number;
    skillName: string;
    year9: number;
    year12: number;
  }[];
  label1?: string;
  label2?: string;
  title?: string;
  subtitle?: string;
  loading?: boolean;
  noShadow?: boolean;
}

export default function AscAnalyseReportSkillsChart({
  className,
  data: _dataProp,
  label1 = "Year 9 Score",
  label2 = "Year 12 Score",
  title = "Skills Improvement",
  subtitle = "Top Skills improved upon taking the Assessment Test in 9th and 12th grade.",
  loading = false,
  noShadow = false,
}: AscAnalyseReportChartsProps) {
  const data: ChartData<"bar", number[], string> = useMemo(
    () => ({
      labels: _dataProp?.map((d) => d.skillName) ?? [],
      datasets: [
        ...(_dataProp?.map((d, index) => {
          const data = new Array(_dataProp?.length).fill(0);
          data.splice(index, 1, d.year12);
          return {
            label: label1,
            data,
            backgroundColor: linearGradient("#FFE9CF", "#ff9b04"),
            order: index * 2 + (d?.year12 > d?.year9 ? 1 : 0),
          };
        }) ?? []),
        ...(_dataProp?.map((d, index) => {
          const data = new Array(_dataProp?.length).fill(0);
          data.splice(index, 1, d.year9);
          return {
            label: label2,
            data,
            backgroundColor: linearGradient("#C3B6FD", "#4239fb"),
            order: index * 2 + (d?.year9 > d?.year12 ? 1 : 0),
          };
        }) ?? []),
      ],
    }),
    [_dataProp, label1, label2]
  );
  const options: Parameters<TypedChartComponent<"bar">>[0]["options"] = useMemo(
    () => ({
      type: "bar",
      responsive: true,
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || "";

              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += `${context.parsed.y}%`;
              }
              return label;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          border: {
            dash: [1, 4],
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            color: "#5E5E73",
            font: {
              family: "Poppins",
              size: 9,
              weight: "bolder",
              lineHeight: 4,
            },
          },
        },
        y: {
          border: {
            dash: [1, 4],
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            // Include a percentage sign in the ticks
            callback: function (value) {
              return `${value}%`;
            },
            color: "#9499B5",
            font: {
              family: "Poppins",
              size: 9,
              weight: "bolder",
              lineHeight: 4,
            },
          },
          max: 100,
        },
      },
      size: {
        width: "100%",
      },
    }),
    []
  );
  return (
    <div
      data-testid="asc-baseline-assessment-chart"
      className={classNames(
        styles.ground,
        {
          [styles.loading]: loading,
        },
        className
      )}
      style={{ boxShadow: noShadow ? "none" : 'unset' }}
    >
      <header className={styles.header}>
        <div>
          <AscHeading className={styles.title} level={4}>
            {title}
          </AscHeading>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <div
              className={styles.legendItemColor}
              style={{
                background:
                  "linear-gradient(360deg, #C3B6FD -50%, #4239fb 150%)",
              }}
            />
            <div className={styles.legendItemLabel}>{label1}</div>
          </div>
          <div className={styles.legendItem}>
            <div
              className={styles.legendItemColor}
              style={{
                background:
                  "linear-gradient(360deg, #FFE9CF -50%, #ff9b04 150%)",
              }}
            />
            <div className={styles.legendItemLabel}>{label2}</div>
          </div>
        </div>
      </header>
      <Bar options={options} data={data} />
    </div>
  );
}

function linearGradient(startColor: string, endColor: string) {
  return function (context: ScriptableContext<"bar">) {
    const chart = context.chart;
    const { ctx, chartArea } = chart;

    if (!chartArea) {
      // This case happens on initial chart load
      return;
    }
    const gradient = ctx.createLinearGradient(
      0,
      chartArea.bottom,
      0,
      chartArea.top
    );
    gradient.addColorStop(0, startColor);
    gradient.addColorStop(1, endColor);
    return gradient;
  };
}
